import React, { useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import * as THREE from 'three';
import ErrorBoundary from '../ErrorBoundary';

interface Rack3DProps {
  rack: any;
  onUnitClick?: (unit: RackUnitInterface | null, position: number) => void;
}

// Individual 3D unit component
const Unit3D: React.FC<{
  unit: RackUnitInterface | null;
  position: number;
  totalUnits: number;
  onClick?: (unit: RackUnitInterface | null, position: number) => void;
}> = ({ unit, position, totalUnits, onClick }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  // Real rack dimensions (in meters for Three.js)
  const RACK_WIDTH = 0.483; // 19 inches = 482.6mm
  const UNIT_HEIGHT = 0.04445; // 1U = 44.45mm
  const UNIT_DEPTH = 0.6; // Typical server depth ~600mm
  const UNIT_MARGIN = 0.002; // 2mm margin between units

  // Calculate position in 3D space with real dimensions
  const yPosition = (totalUnits - position) * (UNIT_HEIGHT + UNIT_MARGIN);

  // Unit colors based on status
  const getUnitColor = () => {
    if (!unit) return '#aaa'; // Gray for empty
    if (unit.device_model) return '#4ade80'; // Green for occupied
    return '#fbbf24'; // Amber for reserved
  };

  return (
    <group position={[0, yPosition, 0]}>
      {/* Main unit body */}
      <mesh
        ref={meshRef}
        onClick={(event) => {
          event.stopPropagation();
          if (onClick) {
            onClick(unit, position);
          }
        }}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
        scale={hovered ? 1.02 : 1}
      >
        <boxGeometry args={[RACK_WIDTH * 0.9, UNIT_HEIGHT, UNIT_DEPTH]} />
        <meshStandardMaterial 
          color={getUnitColor()} 
          opacity={hovered ? 0.8 : 1}
          transparent
        />
      </mesh>

      {/* Unit label */}
      <Text
        position={[0, 0, UNIT_DEPTH / 2 + 0.01]}
        fontSize={0.015}
        color="#171717"
        anchorX="center"
        anchorY="middle"
      >
        {String(position).padStart(2, '0')}
      </Text>

      {/* Device model label if unit has device */}
      {unit?.device_model && (
        <Text
          position={[0, 0, -UNIT_DEPTH / 2 - 0.01]}
          fontSize={0.012}
          color="#444"
          anchorX="center"
          anchorY="middle"
          maxWidth={RACK_WIDTH * 0.8}
        >
          {unit.device_model}
        </Text>
      )}

      {/* Power indicator */}
      {unit?.power_usage && unit.power_usage > 0 && (
        <mesh position={[RACK_WIDTH * 0.4, 0, 0]}>
          <sphereGeometry args={[0.005, 8, 8]} />
          <meshStandardMaterial color="#ef4444" />
        </mesh>
      )}
    </group>
  );
};

// Rack frame component
const RackFrame: React.FC<{ totalUnits: number }> = ({ totalUnits }) => {
  // Real rack dimensions
  const RACK_WIDTH = 0.483; // 19 inches = 482.6mm
  const UNIT_HEIGHT = 0.04445; // 1U = 44.45mm
  const UNIT_MARGIN = 0.002; // 2mm margin between units
  const RAIL_WIDTH = 0.02; // 20mm rail width
  const RAIL_DEPTH = 0.02; // 20mm rail depth
  const PLATE_HEIGHT = 0.01; // 10mm plate thickness
  
  const totalHeight = totalUnits * (UNIT_HEIGHT + UNIT_MARGIN) + 0.1; // Add some padding

  return (
    <group>
      {/* Left rail */}
      <mesh position={[-RACK_WIDTH / 2 - RAIL_WIDTH / 2, totalHeight / 2, 0]}>
        <boxGeometry args={[RAIL_WIDTH, totalHeight, RAIL_DEPTH]} />
        <meshStandardMaterial color="#444" />
      </mesh>

      {/* Right rail */}
      <mesh position={[RACK_WIDTH / 2 + RAIL_WIDTH / 2, totalHeight / 2, 0]}>
        <boxGeometry args={[RAIL_WIDTH, totalHeight, RAIL_DEPTH]} />
        <meshStandardMaterial color="#444" />
      </mesh>

      {/* Top plate */}
      <mesh position={[0, totalHeight + PLATE_HEIGHT / 2, 0]}>
        <boxGeometry args={[RACK_WIDTH + RAIL_WIDTH * 2, PLATE_HEIGHT, 0.7]} />
        <meshStandardMaterial color="#888" />
      </mesh>

      {/* Bottom plate */}
      <mesh position={[0, -PLATE_HEIGHT / 2, 0]}>
        <boxGeometry args={[RACK_WIDTH + RAIL_WIDTH * 2, PLATE_HEIGHT, 0.7]} />
        <meshStandardMaterial color="#888" />
      </mesh>
    </group>
  );
};



// Main 3D rack component
const Rack3DScene: React.FC<Rack3DProps> = ({ rack, onUnitClick }) => {
  const totalUnits = rack?.total_units || 45;
  const [isHovered, setIsHovered] = useState(false);
  
  // Real rack dimensions
  const UNIT_HEIGHT = 0.04445; // 1U = 44.45mm
  const UNIT_MARGIN = 0.002; // 2mm margin between units
  const totalHeight = totalUnits * (UNIT_HEIGHT + UNIT_MARGIN) + 0.1; // Add some padding

  // Calculate camera position to fit entire rack on screen
  const rackAspectRatio = totalHeight / 0.483; // height / width
  const containerAspectRatio = 4 / 3; // h-96 is typically 4:3
  
  let cameraDistance = 1.5;
  let cameraY = totalHeight / 2;
  
  if (rackAspectRatio > containerAspectRatio) {
    // Rack is taller than container, adjust camera distance
    cameraDistance = (totalHeight / 2) / Math.tan((60 * Math.PI / 180) / 2) * 1.2;
    cameraY = totalHeight / 2;
  } else {
    // Rack is wider than tall, adjust camera distance
    cameraDistance = (0.483 / 2) / Math.tan((60 * Math.PI / 180) / 2) * 1.2;
    cameraY = totalHeight / 2;
  }

  // Add key to force re-render when rack changes
  const rackKey = `${rack?.id || 'default'}-${totalUnits}`;

  return (
    <div 
      className="w-full h-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Canvas
        key={rackKey}
        camera={{ 
          position: [
            cameraDistance * (isHovered ? 0.7 : 1), 
            cameraY * (isHovered ? 0.8 : 1), 
            cameraDistance * (isHovered ? 0.7 : 1)
          ], 
          fov: isHovered ? 45 : 60 
        }}
        style={{ background: 'linear-gradient(to bottom, #f8fafc, #e2e8f0)' }}
      >
        {/* Lighting */}
        <ambientLight intensity={0.6} />
        <directionalLight position={[10, 10, 5]} intensity={0.8} />
        <pointLight position={[-10, -10, -5]} intensity={0.3} />

        {/* Controls */}
        <OrbitControls 
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minDistance={isHovered ? 0.3 : 0.5}
          maxDistance={isHovered ? 3 : 5}
          target={[0, totalHeight / 2, 0]}
        />

        {/* Rack frame */}
        <RackFrame totalUnits={totalUnits} />

        {/* Units */}
        {Array.from({ length: totalUnits }, (_, index) => {
          const position = totalUnits - index;
          const unit = rack?.units?.find((u: RackUnitInterface) => u.position === position);
          
          return (
            <Unit3D
              key={position}
              unit={unit}
              position={position}
              totalUnits={totalUnits}
              onClick={onUnitClick}
            />
          );
        })}

        {/* Rack label */}
        <Text
          position={[0, totalHeight + 0.05, 0]}
          fontSize={0.025}
          color="#171717"
          anchorX="center"
          anchorY="middle"
        >
          Rack {rack?.number || 'XX'}
        </Text>
      </Canvas>
    </div>
  );
};

export const Rack3D: React.FC<Rack3DProps> = ({ rack, onUnitClick }) => {
  return (
    <ErrorBoundary>
      <div className="w-full h-96 bg-gradient-to-b from-gray-50 to-gray-100 rounded-lg overflow-hidden">
        <Rack3DScene rack={rack} onUnitClick={onUnitClick} />
      </div>
    </ErrorBoundary>
  );
}; 